// blueprintSaveGuard — assemble a blueprint SAVE payload without fabricating
// graphs this browser never held.
//
// WHERE THE DESTRUCTIVE-WRITE RULE LIVES: the SERVER, not here. A save replaces
// the stored row rather than merging into it, so a graph sent empty deletes the
// server's copy — and the only party that can tell whether that destroys
// anything is the one holding the stored row. avatar-asset-manager's
// app/services/blueprint/save_guard.go (`emptiedGraphs`, ENG-997, on main since
// 2026-08-14) refuses exactly the destructive case: a graph NON-EMPTY in the
// stored row and empty in the payload. It guards both write endpoints — the
// singleton POST (service.go) and the per-graph PATCH (assets.go) — takes an
// `allow_empty` override for a deliberate wipe, and answers 400 with a message
// naming the graph. apiFetch surfaces that message verbatim.
//
// This module USED to carry a client-side port of that rule
// (checkBlueprintSavePayload / confirmedServerEmptyGraphs), written before the
// server had one. It had to guess: its evidence for "is the stored graph
// populated?" was "does a localStorage cache exist for it?", which is a
// different question. The guess locked people out. vaelmor-v2's server body
// graph is legitimately empty (a healthy character with an authored face graph
// and no body graph), the body editor caches `{nodes:[],edges:[]}` 600 ms after
// its canvas mounts whether or not anyone touched it, and a PRESENT-but-empty
// cache was read as corruption — so every save for that character was refused
// locally, forever, and the event-graph edits never reached the server.
// Two clients disagreeing about one rule is worse than one client and one rule.
//
// What remains here is the OTHER half of the ENG-991 fix, which is about
// assembly rather than judgement: never invent a graph. See mergeBlueprintForSave.
//
// Pure module — no imports, unit-tested in tests/unit/blueprintSaveGuard.test.mjs.

/**
 * Assemble a Save-to-Cloud payload from the browser's per-graph localStorage
 * caches, carrying the SERVER's copy through for any graph this browser has
 * no cache for. This is the fix for how the eyeline incident happened
 * (inferred from code + the broken version's shape, ENG-991): saveToCloud
 * used to fabricate `{}` for a cache-missing graph (`bodyData ?? {}`), so a
 * browser that had only edited the EVENT graph — with no body/face cache,
 * e.g. right after the face storage key's v6→v7 bump renamed every existing
 * cache — synced `body:{} face:{}` over the server's full graphs. The
 * all-empty guard (isEmptyBlueprintData) didn't fire because events was
 * non-empty. The server row is replace-not-merge, so the fabricated graphs
 * destroyed the OGS render chain.
 *
 * Local always wins when a cache exists (it's the user's in-flight edit);
 * the server copy only fills the holes. A hole with no server copy falls back
 * to the empty shape, and two things then stand between that and a destroyed
 * graph: BlueprintEditor skips the save outright when NOTHING is populated
 * (isEmptyBlueprintData), and the server refuses any single graph that is
 * populated in the stored row and empty here.
 *
 * @param {{body: object|null, face: object|null, events: object|null}} local
 *   parsed localStorage caches, null where the key is absent
 * @param {object|null} server the current server blueprint (getLatestBlueprint),
 *   or null when unavailable/nonexistent
 */
export function mergeBlueprintForSave(local, server) {
  return {
    body: local?.body ?? server?.body ?? {},
    face: local?.face ?? server?.face ?? {},
    events: local?.events ?? server?.events ?? { eventNodes: [], eventEdges: [], variables: [], functions: {} },
  }
}
